import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import './Home.css';

const Home = () => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [hasVisited, setHasVisited] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const visited = localStorage.getItem('ramson-feeds-visited');
    setHasVisited(!!visited);
    
    if (!visited) {
      localStorage.setItem('ramson-feeds-visited', 'true');
    }
  }, []);

  useEffect(() => {
    if (videoRef.current && isVideoLoaded) {
      if (hasVisited) {
        // Returning user - muted autoplay
        videoRef.current.muted = true;
        setIsMuted(true);
        videoRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(error => {
            console.log('Muted autoplay failed:', error);
            setIsPlaying(false);
          });
      } else {
        // First-time user - try to play with sound
        videoRef.current.muted = false;
        setIsMuted(false);
        videoRef.current.volume = 0.7;
        
        const playWithSound = videoRef.current.play();
        if (playWithSound !== undefined) {
          playWithSound
            .then(() => setIsPlaying(true))
            .catch(error => {
              // If audio autoplay fails, fallback to muted
              console.log('Audio autoplay failed, falling back to muted:', error);
              videoRef.current.muted = true;
              setIsMuted(true);
              videoRef.current.play()
                .then(() => setIsPlaying(true))
                .catch(console.log);
            });
        }
      }
    }
  }, [hasVisited, isVideoLoaded]);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(error => {
            console.log('Play failed:', error);
            setIsPlaying(false);
          });
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  // Handle video end to restart (since it's looped)
  const handleVideoEnd = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(error => {
          console.log('Restart failed:', error);
          setIsPlaying(false);
        });
    }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">

        <video
          className="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload='metadata'
          src="/video/why1.mp4"
          type="video/mp4"
        >
          Your browser does not support the video tag.
        </video>

        <div className="container">
          <h2>{t('welcome')}</h2>
          <p>{t('welcomeDesc')}</p>
          <Link to="/products" className="btn">{t('exploreProducts')}</Link>
        </div>
      </section>

      {/* Products Preview */}
      <section className="products-preview">
        <div className="container">
          <div className="section-title">
            <h2>{t('ourProducts')}</h2>
            <p>{t('discoverRange')}</p>
          </div>
          
          <div className="preview-grid">
            <div className="preview-card">
              <div className="preview-image">
                <img src="/images/bagcover.jpg" alt="Cattle Feed" />
                <div className="preview-overlay">
                  <i className="fas fa-cow"></i>
                  <h3>{t('cattleFeed')}</h3>
                </div>
              </div>
              <div className="preview-content">
                <p>{t('nutritionallyBalanced')}</p>
                <Link to="/products" className="btn">{t('viewAll')}</Link>
              </div>
            </div>
            
            <div className="preview-card">
              <div className="preview-image">
                <img src="/images/rawcover2.png" loading='lazy' alt="Raw Materials" />
                <div className="preview-overlay">
                  <i className="fas fa-seedling"></i>
                  <h3>{t('rawMaterials')}</h3>
                </div>
              </div>
              <div className="preview-content">
                <p>{t('highQualityIngredients')}</p>
                <Link to="/products" className="btn">{t('viewAll')}</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="about-preview">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>{t('whyChooseUs')}</h2>
              <p>{t('whyChooseDesc')}</p>
              
              <div className="benefits">
                <div className="benefit-item">
                  <i className="fas fa-award"></i>
                  <div>
                    <h4>{t('qualityCertified')}</h4>
                    <p>{t('allProductsMeet')}</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <i className="fas fa-truck"></i>
                  <div>
                    <h4>{t('fastDelivery')}</h4>
                    <p>{t('timelyDelivery')}</p>
                  </div>
                </div>
                
                <div className="benefit-item">
                  <i className="fas fa-user-tie"></i>
                  <div>
                    <h4>{t('expertSupport')}</h4>
                    <p>{t('technicalGuidance')}</p>
                  </div>
                </div>
              </div>
              
              <Link to="/about" className="btn">{t('learnMore')}</Link>
            </div>
            
            <div className="about-video">
              <div className="video-container">
                <video
                  ref={videoRef}
                  className="facility-video"
                  loop
                  playsInline
                  preload='metadata'
                  onLoadedData={handleVideoLoad}
                  onEnded={handleVideoEnd}
                  // poster="/images/video-poster.jpg" // Fallback image
                >
                  <source src="/video/why.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <div className="video-controls">
                  <div className="control-group">
                    <button 
                      className="control-btn play-pause-btn"
                      onClick={togglePlayPause}
                      aria-label={isPlaying ? 'Pause video' : 'Play video'}
                    >
                      <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
                    </button>
                    <button 
                      className="control-btn mute-btn"
                      onClick={toggleMute}
                      aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                    >
                      <i className={`fas ${isMuted ? 'fa-volume-mute' : 'fa-volume-up'}`}></i>
                    </button>
                  </div>
                  {/* <div className="video-caption">
                    <i className="fas fa-industry"></i>
                    <p>{t('ourFacilityTour')}</p>
                  </div> */}
                </div>
                {!isVideoLoaded && (
                  <div className="video-loading">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Loading video...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {/* <section className="featured-products">
        <div className="container">
          <div className="section-title">
            <h2>{t('featuredProducts')}</h2>
            <p>{t('featuredDesc')}</p>
          </div>
          
          <div className="products-grid">
            <div className="product-card">
              <div className="product-image">
                <img src="/images/calf-starter.jpg" alt="Premium Calf Starter" />
              </div>
              <div className="product-info">
                <h3>Premium Calf Starter</h3>
                <p>{t('speciallyFormulated')}</p>
              </div>
            </div>
            
            <div className="product-card">
              <div className="product-image">
                <img src="/images/dairy-mix.jpg" alt="Dairy Cow Mix" />
              </div>
              <div className="product-info">
                <h3>Dairy Cow Mix</h3>
                <p>{t('optimizedForMilk')}</p>
              </div>
            </div>
            
            <div className="product-card">
              <div className="product-image">
                <img src="/images/beef-formula.jpg" alt="Beef Cattle Formula" />
              </div>
              <div className="product-info">
                <h3>Beef Cattle Formula</h3>
                <p>{t('promotesWeightGain')}</p>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/products" className="btn">{t('viewAllProducts')}</Link>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default Home;